/* eslint-disable no-unused-vars */
import ShortUniqueId from "short-unique-id";

const uid = new ShortUniqueId();

export const MAP_MISSION_STATUS = {
  ONGOING: "En cours",
  FINISHED: "Terminée",
  ERROR: "Erreur",
  STOPPED: "Arrêtée",
};

export const MAP_MISSION_TYPE: Record<MissionType | "UNKNOWN", string> = {
  TRACE: "Trace",
  TRACE_BACK: "Trace back",
  TRACE_FOLLOW: "Trace follow",
  FOLLOW_LEADER: "Follow the leader",
  TEST: "Status",
  MESSAGE_1: "Message 1",
  MESSAGE_2: "Message 2",
  MESSAGE_3: "Message 3",
  MESSAGE_4: "Message 4",
  MESSAGE_5: "Message 5",
  UNKNOWN: "Inconnue",
  CONFIG_GRAMMAR: "Configuration de la grammaire",
  CONFIG_LED_MATRIX: "Configuration de la matrice de LED",
  TEAM_POSITION: "Team position",
};

export enum MissionsEnum {
  TRACE = "Trace",
  TRACE_BACK = "Trace back",
  TRACE_FOLLOW = "Trace follow",
  FOLLOW_LEADER = "Follow the leader",
  TEAM_POSITION = "Team position",
  MESSAGES = "Messages",
}

export const getMissionKeyByValue = (value: string): string | undefined =>
  Object.keys(MissionsEnum).find((key) => MissionsEnum[key as keyof typeof MissionsEnum] === value);

export enum DeclinationsEnum {
  START = "Départ",
  END = "Fin",
  GUIDANCE = "Guidage",
  APPROACHING_INTERMEDIATE_POINT = "Approche d'un point intermédiaire",
  VALIDATE_POINT = "Validation d'un point",
  PRE_ALERTING_NEXT_POINT = "Pré-alerte du point suivant",
  APPROACHING_ARRIVAL = "Approche de l'arrivée",

  // Messages declinations
  MESSAGE_1 = "Message 1",
  MESSAGE_2 = "Message 2",
  MESSAGE_3 = "Message 3",
  MESSAGE_4 = "Message 4",
  MESSAGE_5 = "Message 5",
}

export const getDeclinationKeyByValue = (value: string): string | undefined =>
  Object.keys(DeclinationsEnum).find((key) => DeclinationsEnum[key as keyof typeof DeclinationsEnum] === value);

export enum VariationDimensionsEnum {
  CONTINU = "Continu",
  SPEED = "Vitesse",
  DISTANCE = "Distance",
  TIME = "Time",
}

export const getDimensionKeyByValue = (value: string): string | undefined =>
  Object.keys(VariationDimensionsEnum).find(
    (key) => VariationDimensionsEnum[key as keyof typeof VariationDimensionsEnum] === value
  );

export const MAP_DIMENSIONS_TO_UNIT: Record<VariationDimensionsEnum, string> = {
  [VariationDimensionsEnum.CONTINU]: "",
  [VariationDimensionsEnum.SPEED]: "m/s",
  [VariationDimensionsEnum.DISTANCE]: "m",
  [VariationDimensionsEnum.TIME]: "s",
};

export enum AnimationLawsEnum {
  DIRECTION = "Direction",
  AZIMUTH = "Azimuth",
  ELEVATION = "Elevation",
  MAP = "Carte",
}

export const getAnimationKeyByValue = (value: string): string | undefined =>
  Object.keys(AnimationLawsEnum).find((key) => AnimationLawsEnum[key as keyof typeof AnimationLawsEnum] === value);

export const generateCorpusScenario = (): CorpusScenarioType => {
  const scenarioId = uid.randomUUID();
  return {
    nbRepetitions: 0,
    label: `Scénario ${scenarioId}`,
    children: [],
    id: scenarioId,
  };
};

export const MAP_MISSION_TO_DECLINATIONS: Record<MissionsEnum, MissionDeclinationType[]> = {
  [MissionsEnum.TRACE]: [
    {
      declinationValue: DeclinationsEnum.START,
      isVariable: false,
      scenarios: [generateCorpusScenario()],
    },
    {
      declinationValue: DeclinationsEnum.END,
      isVariable: false,
      scenarios: [generateCorpusScenario()],
    },
  ],
  [MissionsEnum.TRACE_FOLLOW]: [
    {
      declinationValue: DeclinationsEnum.START,
      isVariable: false,
      scenarios: [generateCorpusScenario()],
    },
    {
      declinationValue: DeclinationsEnum.END,
      isVariable: false,
      scenarios: [generateCorpusScenario()],
    },
    {
      declinationValue: DeclinationsEnum.GUIDANCE,
      isVariable: true,
      dimension: VariationDimensionsEnum.CONTINU,
      scenarios: [generateCorpusScenario()],
    },
    {
      declinationValue: DeclinationsEnum.APPROACHING_INTERMEDIATE_POINT,
      isVariable: true,
      dimension: VariationDimensionsEnum.CONTINU,
      scenarios: [generateCorpusScenario()],
    },
    {
      declinationValue: DeclinationsEnum.VALIDATE_POINT,
      isVariable: false,
      scenarios: [generateCorpusScenario()],
    },
    {
      declinationValue: DeclinationsEnum.PRE_ALERTING_NEXT_POINT,
      isVariable: true,
      dimension: VariationDimensionsEnum.CONTINU,
      scenarios: [generateCorpusScenario()],
    },
    {
      declinationValue: DeclinationsEnum.APPROACHING_ARRIVAL,
      isVariable: true,
      dimension: VariationDimensionsEnum.CONTINU,
      scenarios: [generateCorpusScenario()],
    },
  ],
  [MissionsEnum.TRACE_BACK]: [
    {
      declinationValue: DeclinationsEnum.START,
      isVariable: false,
      scenarios: [generateCorpusScenario()],
    },
    {
      declinationValue: DeclinationsEnum.END,
      isVariable: false,
      scenarios: [generateCorpusScenario()],
    },
    {
      declinationValue: DeclinationsEnum.GUIDANCE,
      isVariable: true,
      dimension: VariationDimensionsEnum.CONTINU,
      scenarios: [generateCorpusScenario()],
    },
    {
      declinationValue: DeclinationsEnum.APPROACHING_INTERMEDIATE_POINT,
      isVariable: true,
      dimension: VariationDimensionsEnum.CONTINU,
      scenarios: [generateCorpusScenario()],
    },
    {
      declinationValue: DeclinationsEnum.VALIDATE_POINT,
      isVariable: false,
      scenarios: [generateCorpusScenario()],
    },
    {
      declinationValue: DeclinationsEnum.PRE_ALERTING_NEXT_POINT,
      isVariable: true,
      dimension: VariationDimensionsEnum.CONTINU,
      scenarios: [generateCorpusScenario()],
    },
    {
      declinationValue: DeclinationsEnum.APPROACHING_ARRIVAL,
      isVariable: true,
      dimension: VariationDimensionsEnum.CONTINU,
      scenarios: [generateCorpusScenario()],
    },
  ],
  [MissionsEnum.FOLLOW_LEADER]: [
    {
      declinationValue: DeclinationsEnum.START,
      isVariable: false,
      scenarios: [generateCorpusScenario()],
    },
    {
      declinationValue: DeclinationsEnum.END,
      isVariable: false,
      scenarios: [generateCorpusScenario()],
    },
    {
      declinationValue: DeclinationsEnum.GUIDANCE,
      isVariable: true,
      dimension: VariationDimensionsEnum.CONTINU,
      scenarios: [generateCorpusScenario()],
    },
  ],
  [MissionsEnum.TEAM_POSITION]: [
    {
      declinationValue: DeclinationsEnum.START,
      isVariable: false,
      scenarios: [generateCorpusScenario()],
    },
    {
      declinationValue: DeclinationsEnum.END,
      isVariable: false,
      scenarios: [generateCorpusScenario()],
    },
    {
      declinationValue: DeclinationsEnum.GUIDANCE,
      isVariable: true,
      dimension: VariationDimensionsEnum.CONTINU,
      scenarios: [generateCorpusScenario()],
    },
  ],
  [MissionsEnum.MESSAGES]: [
    {
      declinationValue: DeclinationsEnum.MESSAGE_1,
      isVariable: false,
      dimension: VariationDimensionsEnum.CONTINU,
      scenarios: [generateCorpusScenario()],
    },
    {
      declinationValue: DeclinationsEnum.MESSAGE_2,
      isVariable: false,
      dimension: VariationDimensionsEnum.CONTINU,
      scenarios: [generateCorpusScenario()],
    },
    {
      declinationValue: DeclinationsEnum.MESSAGE_3,
      isVariable: false,
      dimension: VariationDimensionsEnum.CONTINU,
      scenarios: [generateCorpusScenario()],
    },
    {
      declinationValue: DeclinationsEnum.MESSAGE_4,
      isVariable: false,
      dimension: VariationDimensionsEnum.CONTINU,
      scenarios: [generateCorpusScenario()],
    },
    {
      declinationValue: DeclinationsEnum.MESSAGE_5,
      isVariable: false,
      dimension: VariationDimensionsEnum.CONTINU,
      scenarios: [generateCorpusScenario()],
    },
  ],
};

export const DEFAULT_MISSION_STATUS = "En attente";
